import { api } from './api'
import type { ProcessingJobsResponse, ProcessingJob, DashboardAggregatesResponse, ConflictsResponse, ValidationRulesResponse, IdentityResolutionResponse, ManualReviewErrorsResponse, ManualReviewErrorsParams } from '../types'

export interface ProcessingJobsParams {
  page?: number
  per_page?: number
}

export interface ConflictsParams {
  page?: number
  per_page?: number
  record_id?: string
}

export class ProcessingJobsService {
  /**
   * Get processing jobs summary with pagination
   */
  static async getProcessingJobsSummary(params: ProcessingJobsParams = {}): Promise<ProcessingJobsResponse> {
    try {
      const { page = 1, per_page = 10 } = params

      const response = await api.get<ProcessingJobsResponse>('/processing-jobs/summary', {
        params: {
          page,
          per_page
        }
      })

      return response.data
    } catch (error: any) {
      console.error('Failed to fetch processing jobs:', error)
      throw new Error(error.response?.data?.message || 'Failed to fetch processing jobs')
    }
  }

  /**
   * Get a specific processing job by ID
   */
  static async getProcessingJob(jobId: string): Promise<ProcessingJob> {
    try {
      const response = await api.get<{ success: boolean; data: ProcessingJob }>(`/processing-jobs/${jobId}`)

      if (!response.data.success) {
        throw new Error('Failed to fetch processing job')
      }

      return response.data.data
    } catch (error: any) {
      console.error('Failed to fetch processing job:', error)
      throw new Error(error.response?.data?.message || 'Failed to fetch processing job')
    }
  }

  /**
   * Refresh/retry a failed processing job
   */
  static async retryProcessingJob(jobId: string): Promise<void> {
    try {
      await api.post(`/processing-jobs/${jobId}/retry`)
    } catch (error: any) {
      console.error('Failed to retry processing job:', error)
      throw new Error(error.response?.data?.message || 'Failed to retry processing job')
    }
  }

  /**
   * Get dashboard aggregates for processing jobs stats
   */
  static async getDashboardAggregates(): Promise<DashboardAggregatesResponse> {
    try {
      const response = await api.get<DashboardAggregatesResponse>('/processing-jobs/stats/dashboard-aggregates')

      return response.data
    } catch (error: any) {
      console.error('Failed to fetch dashboard aggregates:', error)
      throw new Error(error.response?.data?.message || 'Failed to fetch dashboard aggregates')
    }
  }

  /**
   * Get conflicts for a specific job with pagination
   */
  static async getJobConflicts(jobId: string | number, params: ConflictsParams = {}): Promise<ConflictsResponse> {
    try {
      const { page = 1, per_page = 50, record_id } = params

      const queryParams: any = {
        page,
        per_page
      }

      // Only include record_id if provided
      if (record_id) {
        queryParams.record_id = record_id
      }

      // Ensure jobId is treated as an integer in the URL path
      const numericJobId = typeof jobId === 'string' ? parseInt(jobId, 10) : jobId

      if (isNaN(numericJobId)) {
        throw new Error('Invalid job ID: must be a valid number')
      }

      const response = await api.get<ConflictsResponse>(`/processing-jobs/${numericJobId}/conflicts`, {
        params: queryParams
      })

      return response.data
    } catch (error: any) {
      console.error('Failed to fetch job conflicts:', error)
      throw new Error(error.response?.data?.message || 'Failed to fetch job conflicts')
    }
  }

  /**
   * Get validation rules for a specific job
   */
  static async getJobValidationRules(jobId: string | number): Promise<ValidationRulesResponse> {
    try {
      // Ensure jobId is treated as an integer in the URL path
      const numericJobId = typeof jobId === 'string' ? parseInt(jobId, 10) : jobId

      if (isNaN(numericJobId)) {
        throw new Error('Invalid job ID: must be a valid number')
      }

      const response = await api.get<ValidationRulesResponse>(`/processing-jobs/${numericJobId}/validation-rules`)

      return response.data
    } catch (error: any) {
      console.error('Failed to fetch job validation rules:', error)
      throw new Error(error.response?.data?.message || 'Failed to fetch job validation rules')
    }
  }

  /**
   * Get identity resolution rules for a specific job
   */
  static async getJobIdentityResolution(jobId: string | number): Promise<IdentityResolutionResponse> {
    try {
      // Ensure jobId is treated as an integer in the URL path
      const numericJobId = typeof jobId === 'string' ? parseInt(jobId, 10) : jobId

      if (isNaN(numericJobId)) {
        throw new Error('Invalid job ID: must be a valid number')
      }

      const response = await api.get<IdentityResolutionResponse>(`/processing-jobs/${numericJobId}/identity-resolution`)

      return response.data
    } catch (error: any) {
      console.error('Failed to fetch job identity resolution rules:', error)
      throw new Error(error.response?.data?.message || 'Failed to fetch job identity resolution rules')
    }
  }

  /**
   * Get manual review errors for a specific job
   */
  static async getJobManualReviewErrors(jobId: string, params: ManualReviewErrorsParams = {}): Promise<ManualReviewErrorsResponse> {
    try {
      const { pending_only, error_category } = params

      const queryParams: any = {}

      if (pending_only !== undefined) {
        queryParams.pending_only = pending_only
      }

      if (error_category) {
        queryParams.error_category = error_category
      }

      const response = await api.get<ManualReviewErrorsResponse>(`/manual-review/job/${jobId}/errors`, {
        params: queryParams
      })

      return response.data
    } catch (error: any) {
      console.error('Failed to fetch job manual review errors:', error)
      throw new Error(error.response?.data?.message || 'Failed to fetch job manual review errors')
    }
  }
}